// COPIA da TMA#718 di CriGio @ 8e60f982cc91900b4e051e8d7f642e628fc9cfe8: src/components/burraco/Carta.tsx — righe 75 (la cornice), 88 (l'indice, `bur-grazie` → `tav-grazie`) e 98-105 (il seme piccolo sotto l'indice e quello grande in basso a destra, con la soglia passata da 48 a 42 px), attraverso `PlayingCard` del CP: sono `DisegnoFrancese`. Il resto e nuovo (la faccia napoletana). Il MINIMO dell'indice (`indiceMinimo`, solo `DisegnoFrancese`) e LOCALE: non viene da CriGio, e un riconfronto con la fonte non e` una deriva.
// `semeInLinea` (qsolitario-orcr-67-semi) e` LOCALE anch'essa: rango e seme sulla STESSA riga in alto, la forma di `angoloInLinea` della Carta del Burraco (components/burraco/Carta.tsx), coi corpi di S9b/KL2 (seme >= 10 px). La DOPPIA FORMA e` un debito dichiarato (C1e), da unire col proprietario del Burraco.
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';

import '@/components/tavolo/tavolo.css';
import { cn } from '@/lib/cn';
import { COLORI_NAPOLETANI, FIGURA, FONDO_2, FONDO_4, FONDO_JOLLY, GLIFO, GLIFO_JOLLY, idCarta, INCHIOSTRO_2, INCHIOSTRO_4, INCHIOSTRO_JOLLY, OMBRA_CARTA, posizioniSemi, rango, sfumato } from '@/lib/tavolo/carta';
import type { Faccia, FacciaFrancese, FacciaNapoletana, MisureCarta, SemeNapoletano } from '@/lib/tavolo/carta';

interface CartaProps {
  /** La faccia (v. `lib/tavolo/carta.ts`). Senza faccia e senza `coperta`, lo slot vuoto. */
  faccia?: Faccia;
  /** Il dorso invece della faccia. */
  coperta?: boolean;
  /** Anello teal forte, la carta si solleva: scelta / presa in mano. */
  scelta?: boolean;
  /** Anello teal tenue: posata in questa mano (non scelta). */
  posata?: boolean;
  /** Anello rosso e la croce: lo scarto. */
  scarto?: boolean;
  /** Sbiadita (nel CP: le file di chi ha fatto foul). */
  sbiadita?: boolean;
  /** Le misure: le taglie le sceglie il gioco. */
  misure: MisureCarta;
  /** Solo francese: un colore per seme invece di rosso e nero. */
  quattroColori?: boolean;
  /** L'etichetta di lettura del dorso quando si tocca (il gioco passa la sua; senza, quella del modulo). */
  etichettaDorso?: string;
  /** Solo jolly: la carta che un jolly calato sostituisce, in piccolo in basso a destra (la dice il game host). Su ogni altra faccia non fa niente. */
  rappresenta?: FacciaFrancese;
  /** Mazzo doppio (scala 40): la COPIA (0 o 1) della carta, per l'id nel DOM (`idCarta(faccia, copia)`). Senza, l'id di sempre. */
  copia?: number;
  /** Solo francese: il corpo minimo dell'indice, in px. Senza, l'indice e` quello di sempre (il «10» si stringe di 3 px sotto i 40 di larghezza); con, mai sotto questo valore. La passa solo la tavola di prova dello Spider (S9: rango >= 11). */
  indiceMinimo?: number;
  /**
   * Solo francese: rango e seme sulla STESSA riga in alto (il seme a destra del rango, mai sotto), per i giochi in colonna dove di una carta si vede solo la striscia in alto. Senza, la faccia e` quella di sempre. Il seme
   * non scende sotto i 10 px (KL2). La passa SOLO la tavola dello Spider a 2 e 4 semi (elenco chiuso in `tools/verify-solitario.ts`, KL4).
   */
  semeInLinea?: boolean;
  onClick?: () => void;
}

/**
 * La CARTA del modulo tavolo, per il mazzo francese (Chinese Poker / OFC, 52) e per il napoletano (scopa e scopone, 40): lo STESSO componente, la stessa cornice.
 * Lo stile e quello del Burraco «premium» (tavole di qchinesepoker-orcb approvate da Luca, I-765): fondo CREMA con una luce appena sfumata, indice con le grazie in alto a
 * sinistra, cornice d'oro sulle carte da 40 px in su, lucido, ombra. Il SEME, invece, e di ciascun mazzo (sotto).
 *  - francese: il disegno di `PlayingCard` del CP, spostato qui senza cambiarlo (il CP lo usa attraverso `PlayingCard`, che ora e un involucro): il seme piccolo sotto l'indice
 *    e, sulle carte da 42 px in su, quello grande in basso a destra;
 *  - napoletano: disegnato da noi sul modello tradizionale (nessuna scansione, nessun asset esterno: SVG scritto qui). L'indice e il GRADO (1..10, anche le figure: e il valore
 *    di presa, uguale in ogni lingua) nel colore del seme; dai 40 px in su, a destra, i semi nella disposizione tradizionale (1..7) o la figura (fante, cavallo, re) col suo
 *    seme; sotto i 40 px un seme solo (v. `DisegnoNapoletano`).
 * Tre forme: la CARTA, il DORSO (una tessera a righe) e lo SLOT vuoto (tratteggiato d'oro).
 * ⛔ Solo presentazione: nessuna regola; l'unico testo e l'etichetta di lettura della carta napoletana («Sette di denari»), dalle lingue.
 */
export function Carta({ faccia, coperta = false, scelta = false, posata = false, scarto = false, sbiadita = false, misure: m, quattroColori = false, etichettaDorso, rappresenta, copia, indiceMinimo, semeInLinea, onClick }: CartaProps) {
  const { t } = useTranslation('general');
  const interactive = Boolean(onClick);
  const misura = { width: m.w, height: m.h, borderRadius: m.raggio };

  // Empty placeholder slot ─────────────────────────────────────────────────
  if (!faccia && !coperta) {
    return (
      <div
        data-slot-vuoto
        className={cn('shrink-0 border border-dashed border-[color-mix(in_srgb,var(--tav-oro)_45%,transparent)] bg-black/20', interactive && 'cursor-pointer active:scale-95')}
        style={misura}
        onClick={onClick}
        role={interactive ? 'button' : undefined}
      />
    );
  }

  // Face-down back ─────────────────────────────────────────────────────────
  if (coperta) {
    const content = (
      <div
        className={cn('shrink-0 border border-[color-mix(in_srgb,var(--tav-oro)_55%,transparent)] bg-[#0d5446]', scelta && 'ring-2 ring-brand')}
        style={{ ...misura, backgroundImage: 'repeating-linear-gradient(45deg, rgba(212,175,106,.18) 0 1.5px, transparent 1.5px 7px)' }}
      />
    );
    if (interactive) {
      return (
        <button type="button" onClick={onClick} className="block" aria-label={etichettaDorso ?? t('carta.coperta')}>
          {content}
        </button>
      );
    }
    return content;
  }

  // Face-up card ───────────────────────────────────────────────────────────
  const f = faccia!;
  // «7 di cuori», «Asso di picche»: il nome a voce di una carta francese, dalle lingue (l'etichetta della carta francese resta `7♥`, com'e).
  const parlata = (c: FacciaFrancese) => {
    const r = rango(c.rango);
    return t('carta.francese', { rango: /^[AJQK]$/.test(r) ? t(`carta.figureFrancesi.${r}`) : r, seme: t(`carta.semiFrancesi.${c.seme}`) });
  };
  const etichetta =
    f.mazzo === 'francese'
      ? `${rango(f.rango)}${GLIFO[f.seme]}`
      : f.mazzo === 'jolly'
        ? rappresenta
          ? t('carta.jollyAlPostoDi', { carta: parlata(rappresenta) })
          : t('carta.jolly')
        : t('carta.napoletana', { grado: t(`carta.gradi.${f.grado}`), seme: t(`carta.semi.${f.seme}`) });
  const grande = m.w >= 40;

  const content = (
    <div
      role="img"
      aria-label={etichetta}
      data-carta={idCarta(f, copia)}
      className={cn(
        'relative shrink-0 select-none overflow-hidden transition-[transform,opacity] duration-150',
        scelta && '-translate-y-2',
        scarto && 'opacity-80',
        sbiadita && 'opacity-45 saturate-50',
        interactive && 'cursor-pointer active:scale-95',
      )}
      style={{ ...misura, background: f.mazzo === 'francese' ? sfumato(quattroColori ? FONDO_4[f.seme] : FONDO_2) : f.mazzo === 'jolly' ? sfumato(FONDO_JOLLY) : sfumato(FONDO_2), boxShadow: ombraFaccia(scelta, posata, scarto) }}
    >
      {grande && <div aria-hidden="true" className="pointer-events-none absolute" style={{ inset: 2.5, borderRadius: Math.max(m.raggio - 2, 2), boxShadow: 'inset 0 0 0 1px rgba(160,125,60,.38)' }} />}
      {f.mazzo === 'francese' ? (
        <DisegnoFrancese f={f} m={m} quattroColori={quattroColori} indiceMinimo={indiceMinimo} semeInLinea={semeInLinea} />
      ) : f.mazzo === 'jolly' ? (
        <DisegnoJolly m={m} rappresenta={rappresenta} quattroColori={quattroColori} />
      ) : (
        <DisegnoNapoletano f={f} m={m} />
      )}
      <div aria-hidden="true" className="tav-lucida" />
      {scarto && (
        <div aria-hidden="true" data-carta-scarto className="pointer-events-none absolute inset-0 flex items-center justify-center bg-danger/15 font-black leading-none text-danger/60" style={{ fontSize: Math.round(m.h * 0.5) }}>
          ✕
        </div>
      )}
    </div>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className="block shrink-0" aria-label={etichetta}>
        {content}
      </button>
    );
  }
  return content;
}

/**
 * Gli anelli degli stati della faccia (qsolitario-orcr-69-anello, condizione (a) di kit-giochi-orcb, SI` di tma-ui-orcb): stanno nel `boxShadow` INLINE, composti con OMBRA_CARTA. Prima erano classi `ring-*` e
 * `shadow-[…]`, che in Tailwind v4 sono `box-shadow` anch'esse: lo stile inline `boxShadow: OMBRA_CARTA` vinceva e non si disegnavano (prova D3 dello Spider, 31/10). I valori sono quelli delle classi.
 * Un anello solo, nell'ordine in cui le classi si sovrapponevano: scarto, poi scelta, poi posata (posata e scelta non si sommano). Il bagliore e` della sola scelta. Il dorso ha il suo anello (classe, senza inline): non passa di qui.
 */
const ANELLO_SCELTA = '0 0 0 2px var(--color-brand)';
const BAGLIORE_SCELTA = '0 0 14px 2px color-mix(in srgb, var(--color-brand) 55%, transparent)';
const ANELLO_POSATA = '0 0 0 1px color-mix(in oklab, var(--color-brand) 60%, transparent)';
const ANELLO_SCARTO = '0 0 0 2px var(--color-danger)';
function ombraFaccia(scelta: boolean, posata: boolean, scarto: boolean): string {
  const anello = scarto ? ANELLO_SCARTO : scelta ? ANELLO_SCELTA : posata ? ANELLO_POSATA : null;
  return [anello, scelta ? BAGLIORE_SCELTA : null, OMBRA_CARTA].filter(Boolean).join(', ');
}

/** L'indice: rango (francese) o grado (napoletano), con le grazie; il 10 si stringe. */
function Indice({ testo, m, colore, indiceMinimo, inRiga = false }: { testo: string; m: MisureCarta; colore: string; indiceMinimo?: number; inRiga?: boolean }) {
  const corpo = testo === '10' && m.w < 40 ? m.rango - 3 : m.rango;
  const fontSize = indiceMinimo === undefined ? corpo : Math.max(corpo, indiceMinimo);
  const letterSpacing = testo === '10' ? '-0.06em' : undefined;
  // in riga (`semeInLinea`) l'indice non si posiziona da solo: lo porta il contenitore della riga
  if (inRiga) {
    return (
      <div className="tav-grazie font-bold leading-none" style={{ fontSize, letterSpacing, color: colore }}>
        {testo}
      </div>
    );
  }
  return (
    <div className="tav-grazie absolute font-bold leading-none" style={{ left: m.dx, top: m.dx, fontSize, letterSpacing, color: colore }}>
      {testo}
    </div>
  );
}

/** Il corpo minimo del seme in riga (KL2 di kit-giochi-orcb, E1 del contratto «Solitari»: >= 10 px; a w=30 il seme della Carta e` 9). */
const SEME_IN_LINEA_MINIMO = 10;

function DisegnoFrancese({ f, m, quattroColori, indiceMinimo, semeInLinea }: { f: FacciaFrancese; m: MisureCarta; quattroColori: boolean; indiceMinimo?: number; semeInLinea?: boolean }) {
  const glifo = GLIFO[f.seme];
  const inchiostro = (quattroColori ? INCHIOSTRO_4 : INCHIOSTRO_2)[f.seme];
  return (
    <>
      {semeInLinea ? (
        // rango e seme sulla stessa riga in alto: «10♠» (il seme a destra, mai sotto il rango)
        <div data-seme-in-linea className="absolute flex items-start whitespace-nowrap leading-none" style={{ left: m.dx, top: m.dx }}>
          <Indice testo={rango(f.rango)} m={m} colore={inchiostro} indiceMinimo={indiceMinimo} inRiga />
          <div aria-hidden="true" className="leading-none" style={{ marginLeft: 1, fontSize: Math.max(m.seme, SEME_IN_LINEA_MINIMO), color: inchiostro }}>
            {glifo}
          </div>
        </div>
      ) : (
        <>
          <Indice testo={rango(f.rango)} m={m} colore={inchiostro} indiceMinimo={indiceMinimo} />
          <div aria-hidden="true" className="absolute leading-none" style={{ left: m.dx, top: m.dx + m.rango + 2, fontSize: m.seme, color: inchiostro }}>
            {glifo}
          </div>
        </>
      )}
      {m.w >= 42 && (
        <div aria-hidden="true" className="absolute leading-none" style={{ right: m.dx - 1, bottom: m.dx - 2, fontSize: Math.round(m.h * 0.42), color: inchiostro, opacity: 0.92 }}>
          {glifo}
        </div>
      )}
    </>
  );
}

/**
 * La faccia JOLLY (scala 40, mazzo da 108): lo stesso viola e lo stesso fondo del jolly del burraco (`components/burraco/Carta.tsx`, chiave `X`), con la stella al posto del seme
 * (una faccia sola per i quattro jolly: il disegno non li distingue). Con `rappresenta` (un jolly calato) la carta che sostituisce sta in piccolo in basso a destra, nel colore del suo seme.
 */
function DisegnoJolly({ m, rappresenta, quattroColori }: { m: MisureCarta; rappresenta?: FacciaFrancese; quattroColori: boolean }) {
  return (
    <>
      <Indice testo={GLIFO_JOLLY} m={m} colore={INCHIOSTRO_JOLLY} />
      {m.w >= 42 && !rappresenta && (
        <div aria-hidden="true" data-jolly-stella className="absolute leading-none" style={{ right: m.dx - 1, bottom: m.dx - 2, fontSize: Math.round(m.h * 0.42), color: INCHIOSTRO_JOLLY, opacity: 0.92 }}>
          {GLIFO_JOLLY}
        </div>
      )}
      {rappresenta && (
        <div
          aria-hidden="true"
          data-rappresenta
          className="absolute whitespace-nowrap font-extrabold leading-none"
          style={{ right: m.dx - 1, bottom: m.dx - 1, fontSize: Math.max(m.seme, 10), color: (quattroColori ? INCHIOSTRO_4 : INCHIOSTRO_2)[rappresenta.seme], letterSpacing: '-0.04em' }}
        >
          {rango(rappresenta.rango)}
          {GLIFO[rappresenta.seme]}
        </div>
      )}
    </>
  );
}

/**
 * La faccia napoletana. 🔴 I semi che si vedono sono SEMPRE quanti ne vanno contati: dai 40 px in su il disegno porta tanti semi quanto il grado (1..7) o la figura col suo
 * seme, e l'indice e il solo numero, nel colore del seme (revisione di kit-giochi-orcb su #734: un seme sotto l'indice, all'altezza della prima fila del disegno, si contava
 * come un seme in piu, «il 2 di denari sembra 3»). Sotto i 40 px non c'e disegno e c'e UN seme solo: grande in basso a destra dai 34 px, piccolo sotto l'indice nella mini.
 */
function DisegnoNapoletano({ f, m }: { f: FacciaNapoletana; m: MisureCarta }) {
  const colori = COLORI_NAPOLETANI[f.seme];
  const grande = m.w >= 40;
  // Il riquadro del disegno (60×80 unita) a destra dell'indice: largo il 62% della carta, appoggiato in basso a destra.
  const arteW = Math.round(m.w * 0.62);
  const arteH = Math.round((arteW * 80) / 60);
  const semeGrande = Math.round(m.h * 0.38);
  const semi = posizioniSemi(f.grado);
  return (
    <>
      <Indice testo={String(f.grado)} m={m} colore={colori.inchiostro} />
      {grande ? (
        <svg aria-hidden="true" data-arte-carta className="absolute" width={arteW} height={arteH} viewBox="0 0 60 80" style={{ right: Math.max(m.dx - 2, 2), bottom: Math.max(m.dx - 1, 2) }}>
          {FIGURA[f.grado] ? <Figura figura={FIGURA[f.grado]} seme={f.seme} /> : semi.punti.map(([x, y], i) => <Seme key={i} seme={f.seme} x={x} y={y} lato={semi.lato} />)}
        </svg>
      ) : m.w >= 34 ? (
        <SemeSvg seme={f.seme} lato={semeGrande} style={{ right: m.dx - 1, bottom: m.dx - 1, opacity: 0.92 }} />
      ) : (
        <SemeSvg seme={f.seme} lato={m.seme} style={{ left: m.dx - 1, top: m.dx + m.rango + 2 }} />
      )}
    </>
  );
}

/** Un seme da solo, in un suo `<svg>` posizionato. */
function SemeSvg({ seme, lato, style }: { seme: SemeNapoletano; lato: number; style: CSSProperties }) {
  return (
    <svg aria-hidden="true" className="absolute" width={lato} height={lato} viewBox="-10 -10 20 20" style={style}>
      <FormaSeme seme={seme} />
    </svg>
  );
}

/** Un seme dentro il disegno, centrato in (x, y), largo `lato` unita. */
function Seme({ seme, x, y, lato }: { seme: SemeNapoletano; x: number; y: number; lato: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${lato / 20})`}>
      <FormaSeme seme={seme} />
    </g>
  );
}

/** I quattro semi, in un riquadro 20×20 centrato nell'origine. */
function FormaSeme({ seme }: { seme: SemeNapoletano }) {
  const { corpo, filo, dettaglio } = COLORI_NAPOLETANI[seme];
  switch (seme) {
    case 'denari':
      return (
        <g data-seme="denari">
          <circle r="9.3" fill={corpo} stroke={filo} strokeWidth="1.2" />
          <circle r="6" fill="none" stroke={filo} strokeWidth="0.9" />
          <circle r="2.4" fill={dettaglio} />
          <circle cx="0" cy="-4.1" r="0.9" fill={filo} />
          <circle cx="4.1" cy="0" r="0.9" fill={filo} />
          <circle cx="0" cy="4.1" r="0.9" fill={filo} />
          <circle cx="-4.1" cy="0" r="0.9" fill={filo} />
        </g>
      );
    case 'coppe':
      return (
        <g data-seme="coppe" stroke={filo} strokeWidth="0.9" strokeLinejoin="round">
          <path d="M-8 -7.5 H8 C8 0 4.5 3 0 3 C-4.5 3 -8 0 -8 -7.5 Z" fill={corpo} />
          <rect x="-8.6" y="-9.6" width="17.2" height="2.6" rx="1.1" fill={dettaglio} />
          <rect x="-1.3" y="3" width="2.6" height="3.8" fill={dettaglio} />
          <path d="M-6 9.6 C-6 7.2 -3 6.6 0 6.6 C3 6.6 6 7.2 6 9.6 Z" fill={dettaglio} />
        </g>
      );
    case 'spade':
      return (
        <g data-seme="spade" stroke={filo} strokeWidth="0.8" strokeLinejoin="round">
          <path d="M0 -10 L2.1 -7 L2.1 3.6 L-2.1 3.6 L-2.1 -7 Z" fill={corpo} />
          <line x1="0" y1="-7.5" x2="0" y2="3" stroke="#dfe8f5" strokeWidth="0.7" />
          <rect x="-6.4" y="3.6" width="12.8" height="2.3" rx="1.1" fill={dettaglio} />
          <rect x="-1.1" y="5.9" width="2.2" height="2.6" fill={filo} />
          <circle cx="0" cy="8.9" r="1.2" fill={dettaglio} />
        </g>
      );
    case 'bastoni':
      return (
        <g data-seme="bastoni" stroke={filo} strokeWidth="0.8" strokeLinejoin="round">
          <path d="M-1.7 9.6 L1.7 9.6 L3.3 -6 C3.5 -9.4 -3.5 -9.4 -3.3 -6 Z" fill={corpo} />
          <circle cx="3.2" cy="-1.6" r="1.1" fill={corpo} />
          <circle cx="-2.9" cy="2" r="1" fill={corpo} />
          <circle cx="2.4" cy="4.6" r="0.9" fill={corpo} />
          <ellipse cx="-3.6" cy="-7.4" rx="2.2" ry="1.1" transform="rotate(-30 -3.6 -7.4)" fill={dettaglio} />
        </g>
      );
  }
}

/** Le figure, nel riquadro 60×80: una sagoma nell'inchiostro del seme e il seme sotto. */
function Figura({ figura, seme }: { figura: 'fante' | 'cavallo' | 're'; seme: SemeNapoletano }) {
  const { inchiostro, filo } = COLORI_NAPOLETANI[seme];
  const oro = '#e0b23a';
  return (
    <g data-figura={figura}>
      {figura === 're' && (
        <g stroke={filo} strokeWidth="1.4" strokeLinejoin="round">
          <path d="M9 34 L13 14 L22 25 L30 9 L38 25 L47 14 L51 34 Z" fill={oro} />
          <rect x="9" y="34" width="42" height="7" rx="1.5" fill={inchiostro} />
          <circle cx="13" cy="13" r="2.4" fill={oro} />
          <circle cx="30" cy="8" r="2.6" fill={oro} />
          <circle cx="47" cy="13" r="2.4" fill={oro} />
        </g>
      )}
      {figura === 'cavallo' && (
        <g stroke={filo} strokeWidth="1.2" strokeLinejoin="round">
          <path d="M46 46 L41 29 C39 25 35 25 31 27 L19 33 C14 35 11 31 14 26 L27 13 L29 5 L34 11 C43 11 50 18 52 29 L54 46 Z" fill={inchiostro} />
          <path d="M34 11 C40 14 44 20 46 28" fill="none" stroke={oro} strokeWidth="2" />
          <circle cx="30" cy="18" r="1.6" fill="#fbf7ec" stroke="none" />
        </g>
      )}
      {figura === 'fante' && (
        <g stroke={filo} strokeWidth="1.2" strokeLinejoin="round">
          <path d="M22 9 L38 9 L35 2 L25 2 Z" fill={oro} />
          <circle cx="30" cy="15" r="6" fill="#f3dcc0" />
          <path d="M21 23 L39 23 L43 46 L17 46 Z" fill={inchiostro} />
          <rect x="22.5" y="46" width="6" height="4" fill={filo} />
          <rect x="31.5" y="46" width="6" height="4" fill={filo} />
          <line x1="21" y1="34" x2="39" y2="34" stroke={oro} strokeWidth="2" />
        </g>
      )}
      <Seme seme={seme} x={30} y={65} lato={24} />
    </g>
  );
}
